export const API_PREFIX = '/api';

const encodePathSegment = (value: string): string => encodeURIComponent(value);

export const Endpoints = {
  auth: {
    me: `${API_PREFIX}/auth`,
    login: `${API_PREFIX}/auth`,
    logout: `${API_PREFIX}/auth`,
    resetPassword: `${API_PREFIX}/resetpwd`,
    resetPasswordConfirm: `${API_PREFIX}/resetpwd`,
  },

  adminDashboard: {
    summary: `${API_PREFIX}/admin`,
  },

  notifications: {
    list: `${API_PREFIX}/projects`,
    adminList: `${API_PREFIX}/admin/projects`,
  },

  notificationPreferences: {
    currentUser: `${API_PREFIX}/notification_preferences`,
    eventTypes: `${API_PREFIX}/notification_preferences/event_types`,
  },

  datasheets: {
    upload: `${API_PREFIX}/datasheets`,
    adminUpload: `${API_PREFIX}/admin/datasheets`,
    one: (id: string) => `${API_PREFIX}/datasheets/${id}`,
    adminOne: (id: string) => `${API_PREFIX}/admin/datasheets/${id}`,
  },

  partNumberAvailability: {
    check: '/api/part_numbers/availability',
  },

  events: {
    list: '/api/events',
    adminList: '/api/admin/events',
    one: (eventId: string) => `/api/events/${eventId}`,
    adminOne: (eventId: string) => `/api/admin/events/${eventId}`,
    adminResellers: '/api/admin/events/resellers',
  },

  users: {
    list: '/api/users',
    adminList: '/api/admin/users',
    self: '/api/users/me',
    adminExport: '/api/admin/users/export',
    one: (userId: string) => `/api/users/${userId}`,
    adminOne: (userId: string) => `/api/admin/users/${userId}`,
    bulkDelete: '/api/users/delete',
    adminBulkDelete: '/api/admin/users/delete',
  },

  projects: {
    list: '/api/projects',
    adminList: '/api/admin/projects',
    adminProductionList: '/api/admin/projects/production',
    adminExport: '/api/admin/projects/export',
    one: (projectId: string) => `/api/projects/${projectId}`,
    adminOne: (projectId: string) => `/api/admin/projects/${projectId}`,
    submitPo: (projectId: string) => `/api/projects/${projectId}/po`,
    adminSubmitPo: (projectId: string) => `/api/admin/projects/${projectId}/po`,
    cableRequirements: (projectId: string) => `/api/projects/${projectId}/cable-requirements`,
    adminCableRequirements: (projectId: string) => `/api/admin/projects/${projectId}/cable-requirements`,
    adminBom: (projectId: string) => `/api/admin/projects/${projectId}/bom`,
    adminBomCsv: (projectId: string) => `/api/admin/projects/${projectId}/bom-csv`,
    adminBomBulk: '/api/admin/projects/bom-bulk',
    adminApproveSales: (projectId: string) => `/api/admin/projects/${projectId}/approve-sales`,
    adminApproveSupport: (projectId: string) => `/api/admin/projects/${projectId}/approve-support`,
    adminRejectPo: (projectId: string) => `/api/admin/projects/${projectId}/reject-po`,
    adminPoFile: (projectId: string) => `/api/admin/projects/${projectId}/po/file`,
    adminStringControllerIpFile: (projectId: string) => (
      `/api/admin/projects/${projectId}/string-controller-ips/file`
    ),
    adminHistory: (projectId: string) => `/api/admin/projects/${projectId}/history`,
    adminMarkInProduction: (projectId: string) => `/api/admin/projects/${projectId}/mark-in-production`,
    adminMarkDelivered: (projectId: string) => `/api/admin/projects/${projectId}/mark-delivered`,
    adminMarkInvoiced: (projectId: string) => `/api/admin/projects/${projectId}/mark-invoiced`,
    adminMarkPaid: (projectId: string) => `/api/admin/projects/${projectId}/mark-paid`,
    shipments: (projectId: string) => (
      `/api/projects/${encodePathSegment(projectId)}/shipments`
    ),
    adminShipments: (projectId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments`
    ),
    adminShipment: (projectId: string, shipmentId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments/${encodePathSegment(shipmentId)}`
    ),
    adminShipmentVoid: (projectId: string, shipmentId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments/${encodePathSegment(shipmentId)}/void`
    ),
    adminShipmentsCloseShort: (projectId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments/close-fulfillment-short`
    ),
    adminShipmentTrackingRefresh: (projectId: string, shipmentId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments/${encodePathSegment(shipmentId)}/refresh-tracking`
    ),
    adminShipmentManifestPreview: (projectId: string) => (
      `/api/admin/projects/${encodePathSegment(projectId)}/shipments/manifest-preview`
    ),
    installationSchedule: (projectId: string) => `/api/projects/${projectId}/installation-schedule`,
    adminInstallationSchedule: (projectId: string) => `/api/admin/projects/${projectId}/installation-schedule`,
    cabinet: (projectId: string, cabinetId: string) => `/api/projects/${projectId}/cabinets/${cabinetId}`,
    adminCabinet: (projectId: string, cabinetId: string) => `/api/admin/projects/${projectId}/cabinets/${cabinetId}`,
    cabinets: (projectId: string) => `/api/projects/${projectId}/cabinets`,
    adminCabinets: (projectId: string) => `/api/admin/projects/${projectId}/cabinets`,
    installationConfig: (projectId: string) => `/api/projects/${projectId}/installation_config`,
    adminInstallationConfig: (projectId: string) => `/api/admin/projects/${projectId}/installation_config`,
    quoteTerms: (projectId: string) => `/api/projects/${projectId}/quote_terms`,
    customerQuoteTerms: (projectId: string) => `/api/projects/${projectId}/customer_quote_terms`,
    adminQuoteTerms: (projectId: string) => `/api/admin/projects/${projectId}/quote_terms`,
    laborConfig: (projectId: string) => `/api/projects/${projectId}/labor_config`,
    pmConfig: (projectId: string) => `/api/projects/${projectId}/pm_config`,
    pmPricingPreview: '/api/projects/pm-pricing-preview',
    travelEstimate: (projectId: string) => `/api/projects/${projectId}/travel-estimate`,
    bulkDelete: '/api/projects/delete',
    adminBulkDelete: '/api/admin/projects/delete',
    exportOne: (projectId: string) => `/api/projects/export/${projectId}`,
    adminExportOne: (projectId: string) => `/api/admin/projects/export/${projectId}`,
    archived: '/api/projects/archived',
    adminArchived: '/api/admin/projects/archived',
    tags: (projectId: string) => `/api/projects/${projectId}/tags`,
    adminTags: (projectId: string) => `/api/admin/projects/${projectId}/tags`,
    archiveOne: (projectId: string) => `/api/projects/${projectId}/archived`,
    adminArchiveOne: (projectId: string) => `/api/admin/projects/${projectId}/archived`,
    roomsAndTablets: (projectId: string) => `/api/projects/${projectId}/rooms_and_tablets`,
    adminRoomsAndTablets: (projectId: string) => `/api/admin/projects/${projectId}/rooms_and_tablets`,
    quotePreview: (projectId: string) => `/api/projects/export/${projectId}/quote-preview`,
    adminQuotePreview: (projectId: string) => `/api/admin/projects/export/${projectId}/quote-preview`,
    specialPricing: (projectId: string) => `/api/projects/${projectId}/special_pricing`,
    adminSpecialPricing: (projectId: string) => `/api/admin/projects/${projectId}/special_pricing`,
    exportPdf: (projectId: string) => `/api/projects/export/${projectId}/pdf`,
    adminExportPdf: (projectId: string) => `/api/admin/projects/export/${projectId}/pdf`,
    exportResellerPdf: (projectId: string) => `/api/projects/export/${projectId}/reseller-pdf`,
    exportDatasheets: (projectId: string) => `/api/projects/export/${projectId}/datasheets`,
    adminExportDatasheets: (projectId: string) => `/api/admin/projects/export/${projectId}/datasheets`,
    partsOptions: (projectId: string) => `/api/projects/${projectId}/parts/options`,
    adminPartsOptions: (projectId: string) => `/api/admin/projects/${projectId}/parts/options`,
    parts: (projectId: string) => `/api/projects/${projectId}/parts`,
    adminParts: (projectId: string) => `/api/admin/projects/${projectId}/parts`,
    adminCustomPricing: (projectId: string) => `/api/admin/projects/${projectId}/custom-pricing`,
    spares: (projectId: string) => `/api/projects/${projectId}/spares`,
    activityQuotes: (projectId: string) => `/api/projects/${projectId}/activity/quotes`,
    adminActivityQuotes: (projectId: string) => `/api/admin/projects/${projectId}/activity/quotes`,
    activityQuotePdf: (projectId: string, quoteNumber: string) => `/api/projects/${projectId}/activity/quotes/${quoteNumber}/pdf`,
    adminActivityQuotePdf: (projectId: string, quoteNumber: string) => `/api/admin/projects/${projectId}/activity/quotes/${quoteNumber}/pdf`,
    messages: (projectId: string) => `/api/projects/${projectId}/messages`,
    adminMessages: (projectId: string) => `/api/admin/projects/${projectId}/messages`,
    messagesRead: (projectId: string) => `/api/projects/${projectId}/messages/read`,
    adminMessagesRead: (projectId: string) => `/api/admin/projects/${projectId}/messages/read`,
  },

  supportProjects: {
    list: '/api/support_projects',
    adminList: '/api/admin/support_projects',
    one: (supportProjectId: string) => `/api/support_projects/${supportProjectId}`,
    adminOne: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}`,
    scheduleRequests: (supportProjectId: string) => `/api/support_projects/${supportProjectId}/schedule-requests`,
    withdrawScheduleRequest: (supportProjectId: string) => (
      `/api/support_projects/${supportProjectId}/schedule-requests/withdraw`
    ),
    completionRequests: (supportProjectId: string) => `/api/support_projects/${supportProjectId}/completion-requests`,
    withdrawCompletionRequest: (supportProjectId: string) => (
      `/api/support_projects/${supportProjectId}/completion-requests/withdraw`
    ),
    messages: (supportProjectId: string) => `/api/support_projects/${supportProjectId}/messages`,
    messagesRead: (supportProjectId: string) => `/api/support_projects/${supportProjectId}/messages/read`,
    files: (supportProjectId: string) => `/api/support_projects/${supportProjectId}/files`,
    adminBulkArchive: '/api/admin/support_projects/archived',
    adminApproveScheduleRequest: (supportProjectId: string) => (
      `/api/admin/support_projects/${supportProjectId}/schedule-requests/approve`
    ),
    adminDenyScheduleRequest: (supportProjectId: string) => (
      `/api/admin/support_projects/${supportProjectId}/schedule-requests/deny`
    ),
    adminDirectSchedule: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/schedule`,
    adminApproveCompletionRequest: (supportProjectId: string) => (
      `/api/admin/support_projects/${supportProjectId}/completion-requests/approve`
    ),
    adminDenyCompletionRequest: (supportProjectId: string) => (
      `/api/admin/support_projects/${supportProjectId}/completion-requests/deny`
    ),
    adminDirectCompletion: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/completion`,
    adminWarranty: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/warranty`,
    adminArchiveOne: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/archived`,
    adminMessages: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/messages`,
    adminMessagesRead: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/messages/read`,
    adminFiles: (supportProjectId: string) => `/api/admin/support_projects/${supportProjectId}/files`,
  },

  partNumbers: {
    adminList: '/api/admin/part_numbers',
    adminExport: '/api/admin/part_numbers/export',
    adminPriceListPdf: '/api/admin/part_numbers/export/pdf',
    adminOne: (id: string) => `/api/admin/part_numbers/${id}`,
    adminDatasheet: (id: string) => `/api/admin/part_numbers/${id}/datasheet`,
  },

  manufacturers: {
    adminList: '/api/admin/manufacturers',
    adminExport: '/api/admin/manufacturers/export',
    adminOne: (manufacturerId: string) => `/api/admin/manufacturers/${manufacturerId}`,
    suggest: '/api/manufacturers/suggest',
  },

  cabinetManufacturers: {
    list: '/api/cabinet_manufacturers',
    adminList: '/api/admin/cabinet_manufacturers',
    adminOne: (cabinetManufacturerId: string) => `/api/admin/cabinet_manufacturers/${cabinetManufacturerId}`,
    suggest: '/api/cabinet_manufacturers/suggest',
  },

  resellers: {
    adminList: '/api/admin/resellers',
    adminExport: '/api/admin/resellers/export',
    adminOne: (resellerId: string) => `/api/admin/resellers/${resellerId}`,
    adminLogoIntent: (resellerId: string) => `/api/admin/resellers/${resellerId}/logo/upload-intent`,
    adminLogoComplete: (resellerId: string) => `/api/admin/resellers/${resellerId}/logo/complete`,
    adminLogoPreview: (resellerId: string) => `/api/admin/resellers/${resellerId}/logo`,
  },

  customers: {
    search: '/api/customers/search',
    create: '/api/customers',
    adminList: '/api/admin/customers',
    adminMerge: (customerId: string) => `/api/admin/customers/${customerId}/merge`,
  },

  myOrganization: {
    root: '/api/my_organization',
    installationPricing: '/api/my_organization/installation_pricing',
    logoIntent: '/api/my_organization/logo/upload-intent',
    logoComplete: '/api/my_organization/logo/complete',
    logoPreview: '/api/my_organization/logo',
    logo: '/api/my_organization/logo',
    quoteTemplates: '/api/my_organization/quote_templates',
    quoteTemplate: (quoteType: string) => `/api/my_organization/quote_templates/${quoteType}`,
    quoteTemplateCoverIntent: (quoteType: string) => (
      `/api/my_organization/quote_templates/${quoteType}/cover-image/upload-intent`
    ),
    quoteTemplateCoverComplete: (quoteType: string) => (
      `/api/my_organization/quote_templates/${quoteType}/cover-image/complete`
    ),
    quoteTemplateCover: (quoteType: string) => `/api/my_organization/quote_templates/${quoteType}/cover-image`,
  },

  customerProfiles: {
    list: '/api/customer_profiles',
    adminList: '/api/admin/customer_profiles',
    adminQueue: '/api/admin/customer_profiles/queue',
    adminAnalytics: '/api/admin/customer_profiles/analytics',
    adminTerminateReseller: '/api/admin/customer_profiles/terminate-reseller',
    one: (profileId: string) => `/api/customer_profiles/${profileId}`,
    adminOne: (profileId: string) => `/api/admin/customer_profiles/${profileId}`,
    adminDecision: (profileId: string) => `/api/admin/customer_profiles/${profileId}/decision`,
    adminQueueOne: (profileId: string) => `/api/admin/customer_profiles/${profileId}/queue`,
    adminPricing: (profileId: string) => `/api/admin/customer_profiles/${profileId}/pricing`,
    compliance: (profileId: string) => `/api/customer_profiles/${profileId}/compliance`,
    adminCompliance: (profileId: string) => `/api/admin/customer_profiles/${profileId}/compliance`,
    audit: (profileId: string) => `/api/customer_profiles/${profileId}/audit`,
    adminAudit: (profileId: string) => `/api/admin/customer_profiles/${profileId}/audit`,
    activities: (profileId: string) => `/api/customer_profiles/${profileId}/activities`,
    adminActivities: (profileId: string) => `/api/admin/customer_profiles/${profileId}/activities`,
    activity: (profileId: string, activityId: string) => `/api/customer_profiles/${profileId}/activities/${activityId}`,
    adminActivity: (profileId: string, activityId: string) => `/api/admin/customer_profiles/${profileId}/activities/${activityId}`,
    statusUpdates: (profileId: string) => `/api/customer_profiles/${profileId}/status-updates`,
    adminStatusUpdates: (profileId: string) => `/api/admin/customer_profiles/${profileId}/status-updates`,
    adminStatusRequests: (profileId: string) => `/api/admin/customer_profiles/${profileId}/status-requests`,
    adminStatusRequestCureNotice: (profileId: string, statusRequestId: string) => `/api/admin/customer_profiles/${profileId}/status-requests/${statusRequestId}/cure-notice`,
    renewals: (profileId: string) => `/api/customer_profiles/${profileId}/renewals`,
    adminRenewals: (profileId: string) => `/api/admin/customer_profiles/${profileId}/renewals`,
    adminRenewalDecision: (profileId: string, renewalId: string) => `/api/admin/customer_profiles/${profileId}/renewals/${renewalId}/decision`,
    complaints: (profileId: string) => `/api/customer_profiles/${profileId}/complaints`,
    adminComplaints: (profileId: string) => `/api/admin/customer_profiles/${profileId}/complaints`,
    complaintResponse: (profileId: string, complaintId: string) => `/api/customer_profiles/${profileId}/complaints/${complaintId}/response`,
    adminComplaintDecision: (profileId: string, complaintId: string) => `/api/admin/customer_profiles/${profileId}/complaints/${complaintId}/decision`,
    adminMoreInfo: (profileId: string) => `/api/admin/customer_profiles/${profileId}/more-info`,
    adminTransfer: (profileId: string) => `/api/admin/customer_profiles/${profileId}/transfer`,
    adminRevoke: (profileId: string) => `/api/admin/customer_profiles/${profileId}/revoke`,
    adminApprovalSlaReminder: (profileId: string) => `/api/admin/customer_profiles/${profileId}/approval-sla-reminder`,
    siteRequests: '/api/customer_profiles/site-requests',
    adminSiteRequests: '/api/admin/customer_profiles/site-requests',
    adminSiteRequestDecision: (requestId: string) => `/api/admin/customer_profiles/site-requests/${requestId}/decision`,
  },

  files: {
    uploadIntent: '/api/files/upload_intents',
    complete: '/api/files/complete',
    forProject: (projectId: string) => `/api/admin/projects/${projectId}/files`,
  },

  batteries: {
    list: '/api/batteries',
    adminList: '/api/admin/batteries',
    export: '/api/batteries/export',
    adminExport: '/api/admin/batteries/export',
    one: (batteryId: string) => `/api/batteries/${batteryId}`,
    adminOne: (batteryId: string) => `/api/admin/batteries/${batteryId}`,
    replacementDetails: (batteryId: string) => `/api/batteries/${batteryId}/replacement-details`,
    adminDelete: (batteryId: string) => `/api/admin/batteries/${batteryId}`,
    archiveOne: (batteryId: string) => `/api/batteries/${batteryId}/archived`,
    adminArchiveOne: (batteryId: string) => `/api/admin/batteries/${batteryId}/archived`,
    bulkArchive: '/api/batteries/archived',
    adminBulkArchive: '/api/admin/batteries/archived',
    suggest: '/api/batteries/suggest',
  },

  batteryCabinetModels: {
    list: '/api/battery_cabinet_models',
    adminList: '/api/admin/battery_cabinet_models',
    one: (batteryCabinetModelId: string) => `/api/battery_cabinet_models/${batteryCabinetModelId}`,
    adminOne: (batteryCabinetModelId: string) => `/api/admin/battery_cabinet_models/${batteryCabinetModelId}`,
    adminDelete: (batteryCabinetModelId: string) => `/api/admin/battery_cabinet_models/${batteryCabinetModelId}`,
    adminArchiveOne: (batteryCabinetModelId: string) => `/api/admin/battery_cabinet_models/${batteryCabinetModelId}/archived`,
    adminBulkArchive: '/api/admin/battery_cabinet_models/archived',
  },

  cables: {
    adminList: '/api/admin/cables',
    adminOne: (cableId: string) => `/api/admin/cables/${cableId}`,
    adminBulkArchive: '/api/admin/cables/archived',
    adminArchiveOne: (cableId: string) => `/api/admin/cables/${cableId}/archived`,
  },

  flameArrestors: {
    list: '/api/flame_arrestors',
    adminList: '/api/admin/flame_arrestors',
    one: (flameArrestorId: string) => `/api/flame_arrestors/${flameArrestorId}`,
    adminOne: (flameArrestorId: string) => `/api/admin/flame_arrestors/${flameArrestorId}`,
    archiveOne: (flameArrestorId: string) => `/api/flame_arrestors/${flameArrestorId}/archived`,
    adminArchiveOne: (flameArrestorId: string) => `/api/admin/flame_arrestors/${flameArrestorId}/archived`,
    bulkArchive: '/api/flame_arrestors/archived',
    adminBulkArchive: '/api/admin/flame_arrestors/archived',
    suggest: '/api/flame_arrestors/suggest',
  },
} as const;
